import React, { useState, useEffect } from 'react';
import { makeStyles, createStyles } from '@material-ui/core';
import {
	NeonGrid,
	Pagination,
	PageRange,
	Link,
	MessageView,
	Loader,
	ExtendedTheme,
	UserInterfaceCheck,
	IconButton,
	UserInterfaceCheckCircleOutline,
	UserInterfaceCrossCircleOutline
} from 'neon-design-system';
import ListPageHeader from '../ListPageHeader';
import NotificationMessage from '../NotificationMessage';
import { ColDef, GridOptions, GridApi, ColumnApi } from 'ag-grid-community';
import { ApolloError } from 'apollo-client';
import { Router, useHistory } from 'react-router-dom';
import { StringValueNode } from 'graphql';
import { IdAndName } from '../../../shared/types';
import { History } from 'history';
import * as Enterprise from 'ag-grid-enterprise';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { premiumChannelsEnabled } from '../../../utils/utils';
import { FilterContextData, FilterType } from '../../providers/admin/ArticleFilterProvider';

const agGridLicense: string | undefined = process.env.REACT_APP_AGGRID_LICENSE;

if (agGridLicense) {
	Enterprise.LicenseManager.setLicenseKey(agGridLicense);
}

interface StyleProps {
	showHeader: boolean;
	fullWidth: boolean;
	removeBottomOffset?: boolean;
}

const useStyles = makeStyles<ExtendedTheme, StyleProps>(theme => createStyles({
	container: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		padding: (props) => !props.showHeader ? 0 : `0 ${theme.spacing(2)}px`,
		marginBottom: (props) => props.removeBottomOffset ? 0 : theme.spacing(6)
	},
	main: {
		flex: 1,
		flexDirection: 'column',
		maxWidth: ({ fullWidth }) => fullWidth ? '100%' : 1200
	},
	list: {
		marginTop: theme.spacing(2),
		overflow: 'hidden',
		minHeight: 244,
		flexGrow: 1,
		'& .ag-cell': {
			display: 'flex'
		},
		'& .ag-theme-balham-dark .ag-header-cell-text': {
			...theme.customTypography.footnote.medium,
			fontWeight: theme.typography.fontWeightBold,
			color: theme.themeColors.grey['400']
		},
		'& .ag-theme-balham-dark .ag-row-group-leaf-indent.ag-row-group-indent-0': {
			marginLeft: theme.spacing(5)
		},
		'& .ag-theme-balham-dark .ag-row-group-indent-1': {
			paddingLeft: theme.spacing(4.5),
			'& .ag-group-expanded, & .ag-group-contracted': {
				marginLeft: `-${theme.spacing(1.625)}px`
			}
		},
		'& .ag-theme-balham-dark .ag-row-group-indent-2': {
			paddingLeft: theme.spacing(7.5),
			'& .ag-group-expanded, & .ag-group-contracted': {
				marginLeft: `-${theme.spacing(1.5)}px`
			}
		},
		'& .ag-group-expanded, & .ag-group-contracted': {
			width: 'auto',
			marginRight: theme.spacing(1.3),
			'& .ag-icon': {
				fontWeight: 'bold',
				fontSize: '18px'
			}
		},
		'& .ag-theme-balham-dark .ag-row-group': {
			color: 'inherit'
		},
		'& .ag-theme-balham-dark .ag-row-group .ag-cell-range-left': {
			color: theme.themeColors.grey['400']
		}
	},
	page: {
		marginTop: theme.spacing(2)
	},
	info: {
		marginTop: theme.spacing(2)
	},
	noRecords: {
		marginTop: theme.spacing(2),
		backgroundColor: theme.palette.grey[800],
		borderRadius: 4,
		height: 244,
		display: 'flex',
		flexDirection: 'column'
	},
	noRecordsMessage: {
		marginTop: theme.spacing(8)
	},
	loader: {
		display: 'flex',
		flexDirection: 'column',
		height: 244,
		position: 'relative'
	},
	paragraph: {
		marginTop: theme.spacing(2)
	},
	fullWidth: {
		width: '100%'
	},
	noWrap: {
		display: 'block',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	wrap: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
		whiteSpace: 'normal !important' as 'normal',
		overflowWrap: 'break-word !important' as 'break-word'
	}
}));

const ranges: PageRange[] = [
	{ label: '15', value: 15 },
	{ label: '30', value: 30 },
	{ label: '50', value: 50 },
	{ label: '100', value: 100 },
	{ label: '200', value: 200 }
];

export interface AdminListProps<T> {
	items: T[];
	name?: string;
	title?: string;
	columns?: ColDef[];
	autoGroupColumn?: ColDef;
	showAdd: boolean;
	aboveTable?: React.ReactNode;
	toolbar?: React.ReactNode;
	loading: boolean;
	error?: ApolloError;
	cellRenderers?: any;
	showHeader?: boolean;
	treeData?: boolean;
	addButtonLabel?: string;
	secondaryButtonLabel?: string;
	entityForCaptionText?: string;
	secondaryButtonUrl?: string;
	itemsNumber?: number;
	listPageHeader?: boolean;
	gridOptions?: GridOptions;
	defaultColDef?: ColDef;
	apiCallback?: (api: GridApi, columnApi: ColumnApi) => void;
	getRowHeight?: (p: any) => number | undefined;
	fullWidth?: boolean;
	showCaption?: boolean;
	overrideContainerClass?: string;
	overrideListClass?: string;
	pagination?: boolean;
	overwritePagination?: boolean;
	overwriteCurrentPage?: number;
	overwritePageRange?: number;
	onCurrentPageChange?: (page: number) => void;
	onPerPageChange?: (page: number) => void;
	showTableWhenEmpty?: boolean;
	removeBottomOffset?: boolean;
}

const AdminList = <T,>({
	items,
	name,
	title,
	columns,
	showAdd,
	aboveTable,
	toolbar,
	loading,
	cellRenderers,
	showHeader = true,
	treeData = false,
	addButtonLabel = '',
	secondaryButtonLabel = '',
	entityForCaptionText = '',
	secondaryButtonUrl = '',
	autoGroupColumn = {},
	itemsNumber = undefined,
	listPageHeader = true,
	gridOptions: customGridOptions = {},
	defaultColDef: customDefaultColDef = {},
	apiCallback = () => { },
	getRowHeight,
	fullWidth = false,
	showCaption = true,
	overrideContainerClass,
	overrideListClass,
	pagination = true,
	showTableWhenEmpty = true,
	overwritePagination = false,
	overwriteCurrentPage,
	overwritePageRange,
	onCurrentPageChange = () => { },
	onPerPageChange = () => { },
	removeBottomOffset
}: AdminListProps<T>): JSX.Element => {
	const classes = useStyles({ showHeader: !!showHeader, fullWidth: fullWidth, removeBottomOffset });
	const history = useHistory();
	const defaultRange = overwritePageRange ? ranges.find(r => r.value === overwritePageRange) || ranges[0] : !treeData ? ranges[0] : ranges[3];
	const [selectedRange, setSelectedRange] = useState(defaultRange);
	const [currentPage, setCurrentPage] = useState(overwriteCurrentPage || 1);
	const [grid, setGrid] = useState<GridApi | undefined>();

	const rowsPerPage = selectedRange.value;

	const grOptions: GridOptions = {
		suppressContextMenu: true,
		suppressAutoSize: false,
		suppressHorizontalScroll: true,
		suppressScrollOnNewData: true,
		pagination: true,
		paginationPageSize: rowsPerPage,
		suppressPaginationPanel: true,
		rowHeight: 40,
		accentedSort: true,
		getRowNodeId: (data) => data?._id,
		onGridReady: (event: any) => {
			setGrid(event.api);
			apiCallback(event.api, event.columnApi);
			event.api.sizeColumnsToFit();
			event.api.resetRowHeights();
		},
		onSortChanged: (event: any) => {
			setCurrentPage(1);
		},
		treeData,
		autoGroupColumnDef: autoGroupColumn,
		groupDefaultExpanded: -1,
		getDataPath: (data) => (
			data.categoriesHierarchy
		),
		frameworkComponents: cellRenderers,
		defaultColDef: {
			autoHeight: false,
			suppressMovable: true,
			sortable: true,
			cellStyle: {
				'white-space': 'nowrap !important',
				'text-overflow': 'ellipsis',
				'overflow': 'hidden',
				'min-height': '40px',
				'padding': '12px',
				'line-height': '16px',
				'border': 'none',
				'font-feature-settings': 'normal',
				'align-items': 'center'
			},
			...customDefaultColDef
		},
		...customGridOptions
	};

	useEffect(function gotoFirstPageWhenFiltersChange() {
		if (overwritePagination) return;
		setCurrentPage(1);
	}, [items]);

	useEffect(() => {
		if (overwritePagination) return;
		grid?.paginationGoToPage(currentPage - 1);
	}, [grid, currentPage]);

	useEffect(() => {
		if (overwritePagination) return;
		setCurrentPage(1);
		grid?.paginationSetPageSize(rowsPerPage);
	}, [grid, rowsPerPage]);

	useEffect(() => {
		onCurrentPageChange(currentPage);
	}, [currentPage]);

	useEffect(() => {
		overwriteCurrentPage && setCurrentPage(overwriteCurrentPage);
	}, [overwriteCurrentPage]);

	useEffect(() => {
		onPerPageChange(rowsPerPage);
	}, [rowsPerPage]);

	const itemsLength = itemsNumber ? itemsNumber : items.length;
	const pageCaption = showCaption ? getCaption(currentPage, itemsLength, itemsLength, rowsPerPage, !treeData) : undefined;
	const addNewUrl = showAdd && name ? `${baseUrls.publisher.main}/${name.toLowerCase()}/add` : '';
	const pageHeaderTitle = title ? title : name;

	if (itemsLength === 0 && !loading) {
		return (
			<div className={[classes.container, overrideContainerClass].filter(n => !!n).join(' ')}>
				<div className={classes.main}>
					<div className={classes.header}>
						<ListPageHeader
							title={pageHeaderTitle}
							entityForCaptionText={entityForCaptionText}
							addButtonLabel={addButtonLabel}
							secondaryButtonLabel={secondaryButtonLabel}
							showAdd={showAdd}
							showSecondaryButton={!!secondaryButtonLabel}
							secondaryButtonUrl={secondaryButtonUrl}
							addNewUrl={addNewUrl}
							toolbar={toolbar}
							pageCaption={pageCaption}
							showHeader={showHeader}
						/>
					</div>
					{aboveTable}
					{showTableWhenEmpty &&
						<div className={classes.noRecords}>
							<div className={classes.noRecordsMessage}>
								<MessageView styleName="grey" size="large" >
									There are no items to show
								</MessageView>
							</div>
						</div>
					}
				</div>
				<div>
					<NotificationMessage />
				</div>
			</div>
		);
	}

	return (
		<div className={[classes.container, overrideContainerClass].filter(n => !!n).join(' ')}>
			<div className={classes.main}>
				{listPageHeader &&
					<div className={classes.header}>
						<ListPageHeader
							title={pageHeaderTitle}
							entityForCaptionText={entityForCaptionText}
							addButtonLabel={addButtonLabel}
							secondaryButtonLabel={secondaryButtonLabel}
							showAdd={showAdd}
							showSecondaryButton={!!secondaryButtonLabel}
							secondaryButtonUrl={secondaryButtonUrl}
							addNewUrl={addNewUrl}
							toolbar={toolbar}
							pageCaption={pageCaption}
							showHeader={showHeader}
						/>
					</div>
				}

				<div>
					<NotificationMessage />
				</div>
				{aboveTable}
				<div className={[classes.list, overrideListClass].filter(n => !!n).join(' ')}>
					{loading
						? <div className={classes.loader}>
							<Loader />
						</div>
						: <NeonGrid
							rowData={items}
							domLayout="autoHeight"
							columnDefs={columns}
							context={{ history }}
							onRowDataChanged={onRowDataChanged}
							gridOptions={grOptions}
							getRowHeight={getRowHeight}
						/>
					}
				</div>

				{pagination && !loading && !treeData &&
					<div className={classes.page}>
						<Pagination
							id={'admin-list-pagination'}
							ranges={ranges}
							onRangeChanged={setSelectedRange}
							selectedRange={selectedRange}
							currentPage={currentPage}
							maxPage={Math.ceil(itemsLength / rowsPerPage)}
							totalRecords={itemsLength}
							recordsPerPage={rowsPerPage}
							onPageChanged={(delta: number): void => setCurrentPage(currentPage + delta)} />
					</div>
				}
			</div>
		</div>
	);
};

export default AdminList;

function getCaption(
	currentPage: number, selectedRecords: number, totalRecords: number, perPage: number, showRange = true
): string {
	if (selectedRecords === 0) {
		return '0';
	}

	if (!showRange) {
		return `${totalRecords}`;
	}

	const from = (currentPage - 1) * perPage + 1;
	const to = Math.min(from + perPage - 1, totalRecords);

	return `${from}-${to} of ${totalRecords}`;
}

export const TemplateNameCellRenderer = (props: any): JSX.Element => {
	const path = !props.addFromTemplate ?
		`${baseUrls.publisher.main}/${props.apiName}/edit/${window.encodeURIComponent(props.data._id)}` :
		`${baseUrls.publisher.articles.addFromTemplate}/${window.encodeURIComponent(props.data._id)}`;

	if (!props.context?.history) throw new Error('React router history must be added to ag-grid context');

	return (
		<Router history={props.context.history}>
			{
				props.data.publishedBy ?
					<LinkCellRenderer url={path} label={props.value} history={props.context?.history} {...props} /> :
					props.value
			}
		</Router>
	)
}

export const NameCellRenderer = (props: any): JSX.Element => {
	const toPath = `${baseUrls.publisher.main}/${props.apiName.toLowerCase()}/edit/${window.encodeURIComponent(props.data._id)}`;

	if (!props.context?.history) throw new Error('React router history must be added to ag-grid context');

	const label = props.data.parent ? '— ' + props.value : props.value;

	return (
		<LinkCellRenderer url={toPath} label={label} history={props.context?.history} {...props} />
	);
};

export const ReadOnlyLabelCellRenderer = (props: any): JSX.Element => {
	return ((!props.data.parent) ? props.value : '— ' + props.value);
};

export const SubscribersCellRenderer = (props: any): JSX.Element => {
	const classes = useStyles(props);

	const label = (props.value === 0)
		? 'No users'
		: props.value + ' user' + (props.value > 1 ? 's' : '');

	return (
		<div className={classes.fullWidth}>
			{
				props.value === 0 ?
					label :
					<LinkCellRenderer
						url={`${premiumChannelsEnabled() ? 'topics' : 'categories'}/subscriber-stats/${props.data._id}`}
						label={label}
						history={props.context?.history}
						boldLabel={false}
						{...props}
					/>
			}
		</div>
	);
};

export const CheckIconCellRendered = (props: any): JSX.Element => {
	if (!props.value) {
		return <></>;
	}

	return <UserInterfaceCheck />
};

export interface LinkCellProps {
	url: string;
	label: StringValueNode;
	history: any;
}

export const LinkCellRenderer = (props: any): JSX.Element => {
	const classes = useStyles(props);
	const {
		history,
		label,
		wrap = false,
		boldLabel = true
	} = props;

	if (!history) throw new Error('React router history must be added to ag-grid context');

	return (
		<div className={classes.fullWidth}>
			<Router history={props.history}>
				<Link className={wrap ? classes.wrap : classes.noWrap} to={props.url} {...props}>
					{
						boldLabel ? <strong>{label}</strong> : label
					}
				</Link>
			</Router>
		</div>
	);
};

const useStylesLink = makeStyles<ExtendedTheme>(theme => createStyles({
	entityLinkList: {
		width: '100%',
		display: 'flex',
		flexWrap: 'wrap'
	}
}));

interface ArticleCountCellRendererProps {
	filter: FilterContextData;
	value: number;
	context?: any;
	filterData: {
		_id: string;
		name: string;
		type: FilterType;
	};
}

export const ArticleCountCellRenderer = (props: ArticleCountCellRendererProps): JSX.Element => {

	const classes = useStylesLink();

	if (props.value === 0) {
		return <div>No articles</div>
	}

	if (!props.context?.history) throw new Error('React router history must be added to ag-grid context');

	const articleFilter = props.filter;
	const label = props.value ? props.value + ' article' + (props.value > 1 ? 's' : '') : 'Loading…';
	const toPath = baseUrls.publisher.articles.list;

	const clickHandler = () => {
		const filter = props.filterData;
		articleFilter.clear();
		articleFilter.add(filter);
		props.context?.history.push(toPath);
	}

	return (
		<div className={classes.entityLinkList}>
			<Router history={props.context?.history}>
				<Link onClick={clickHandler} >{label}</Link >
			</Router>
		</div>
	);
};

interface ArticleViewCellRendererProps {
	value: number;
	linkTo?: 'articles';
	data: IdAndName;
	context: {
		history: History;
	};
}

interface ReadOnlyArticleViewCellRendererProps {
	value: number;
}

export const ReadOnlyArticleViewCellRenderer = (props: ReadOnlyArticleViewCellRendererProps): JSX.Element => {
	if (props.value === 0) {
		return <div>No views</div>
	}

	return <div>{props.value + ' view' + (props.value === 1 ? '' : 's')}</div>;
};

export const ArticleViewCellRenderer = (props: ArticleViewCellRendererProps): JSX.Element => {

	if (!props.context?.history) throw new Error('React router history must be added to ag-grid context');

	if (props.value === 0) {
		return <div>No views</div>
	}

	const label = props.value ? props.value + ' view' + (props.value === 1 ? '' : 's') : 'Loading…';
	const url = props.linkTo && `${baseUrls.publisher.main}/${props.linkTo}/stats/${props.data._id}`;

	return (
		<Router history={props.context?.history}>
			{url ? <Link to={url}>{label}</Link> : label}
		</Router>
	);
};



const useDecisionStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => createStyles({
	approveButton: {
		color: theme.themeColors.success.main,
		order: 0,
		flexGrow: 0,
		width: 'auto',
		minWidth: '1px'
	},
	rejectButton: {
		color: theme.themeColors.error.main,
		order: 1,
		flexGrow: 0,
		width: 'auto',
		minWidth: '1px'
	},
	decision: {
		display: 'flex',
		flexDirection: 'row',
		gap: '16px',
		flex: 'none',
		order: 4,
		flexGrow: 0
	}
}));

interface DecisionCellRendererProps {
	onYesClick: () => void;
	onNoClick: () => void;
}

export const DecisionCellRenderer = ({ onYesClick, onNoClick }: DecisionCellRendererProps): JSX.Element => {
	const classes = useDecisionStyles();
	return (
		<div className={classes.decision} >
			<IconButton
				className={classes.approveButton}
				onClick={onYesClick}
			>
				<UserInterfaceCheckCircleOutline />
			</IconButton>
			<IconButton
				className={classes.rejectButton}
				onClick={onNoClick}
			>
				<UserInterfaceCrossCircleOutline />
			</IconButton>
		</div>
	);
}

function onRowDataChanged(event: any) {
	event.api.resetRowHeights();
}

export const sortColByStringProp = <T extends { [P in K]: string }, K extends keyof T>(field: K) =>
	(valueA: T | undefined, valueB: T | undefined) => {
		if (!valueA?.[field]) return -1;
		if (!valueB?.[field]) return 1;
		console.log(valueA?.[field], valueB?.[field], valueA[field].localeCompare(valueB[field] ?? ''));
		return valueA[field].localeCompare(valueB[field] ?? '');
	};
